
/* ===== Component: Layout – SolutionsDropdown ===== */
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

/**
 * Props for the SolutionsDropdown component
 * @interface SolutionsDropdownProps
 * @property {boolean} mobile - Whether this dropdown is being used in the mobile navigation
 */
interface SolutionsDropdownProps {
  mobile?: boolean;
}

/**
 * SolutionsDropdown component that provides navigation to different solution pages
 * Can be displayed in both desktop and mobile navigation contexts
 * 
 * @param {SolutionsDropdownProps} props - Component properties
 * @returns {JSX.Element} Dropdown navigation for solutions
 */
const SolutionsDropdown = ({ mobile = false }: SolutionsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  /**
   * Toggles the dropdown open/closed state
   */
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  /**
   * Closes the dropdown
   */
  const closeDropdown = () => {
    setIsOpen(false);
  };

  /**
   * Effect to close dropdown when clicking outside of it
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Solutions data for the dropdown menu
   */
  const solutions = [
    { 
      title: 'Fibre-Optic Perimeter', 
      path: '/solutions/fibre',
      description: 'Buried, unseen perimeter detection systems'
    },
    { 
      title: 'On-Fence Detection', 
      path: '/solutions/fence',
      description: 'Transform existing fences into smart sentinels'
    },
    { 
      title: 'Bespoke Security', 
      path: '/solutions/bespoke',
      description: 'Custom solutions for unique properties'
    },
    {
      title: 'Industries We Serve',
      path: '/industries',
      description: 'Specialized solutions for various sectors'
    }
  ];

  /**
   * Mobile version of the dropdown (expanded list)
   */
  if (mobile) {
    return (
      <div className="space-y-1">
        <Link to="/solutions" className="flex w-full py-2 text-white hover:text-forti-fi-teal">
          Solutions Overview
        </Link>
        {solutions.map((solution, index) => (
          <Link 
            key={index} 
            to={solution.path} 
            className="flex w-full py-1 pl-6 text-forti-fi-lavender hover:text-forti-fi-teal"
            onClick={closeDropdown}
          >
            {solution.title}
          </Link>
        ))}
      </div>
    );
  }

  /**
   * Desktop version of the dropdown
   */
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="inline-flex items-center gap-1 px-4 py-2 text-forti-fi-charcoal hover:text-forti-fi-DEFAULT focus:outline-none"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        Solutions
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute left-0 z-10 mt-2 w-72 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-forti-fi-DEFAULT/20 focus:outline-none">
          <div className="py-1">
            <Link 
              to="/solutions"
              className="block px-4 py-3 text-sm border-b border-gray-100 text-forti-fi-DEFAULT font-medium hover:bg-forti-fi-lavender/10"
              onClick={closeDropdown}
            >
              Solutions Overview
            </Link>
            
            {solutions.map((solution, index) => (
              <Link
                key={index}
                to={solution.path}
                className="block px-4 py-3 text-sm hover:bg-forti-fi-lavender/10"
                onClick={closeDropdown}
              >
                <div className="font-medium text-forti-fi-charcoal">{solution.title}</div>
                <div className="text-xs mt-0.5 text-forti-fi-charcoal/70">{solution.description}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SolutionsDropdown;
